import { GET_REPORT_STATUS } from '@/lib/graphql/reports';

export interface ApolloGraphqlClient {
  mutate(options: {
    errorPolicy?: string;
    fetchPolicy?: string;
    mutation: unknown;
    variables?: Record<string, unknown>;
  }): Promise<{ data?: unknown }>;
}

export interface ReportLog {
  context?: null | string;
  step: string;
  timestamp: string;
}

export type ReportStatus = 'completed' | 'failed' | 'in_progress' | 'pending';

interface GetReportStatusPayload {
  errorMessage?: null | string;
  generationStatus?: null | string;
  message?: null | string;
  reportLogs?: null | ReportLog[];
}

export function encodeReportId(reportId: number): string {
  return base64EncodeAscii(String(reportId));
}

export function normalizeReportStatus(raw?: null | string): ReportStatus {
  const s = (raw ?? '').trim().toLowerCase();
  if (s === 'completed' || s === 'complete') return 'completed';
  if (s === 'failed' || s === 'failure' || s === 'error') return 'failed';
  if (s === 'pending' || s === 'queued') return 'pending';
  return 'in_progress';
}

export async function pollReportStatus(params: {
  client: ApolloGraphqlClient;
  reportId: number;
  reportType: 'ewr' | 'loan_tape' | 'pid';
}): Promise<{
  error?: null | string;
  logs: ReportLog[];
  status: ReportStatus;
}> {
  const encodedId = encodeReportId(params.reportId);

  const result = await params.client.mutate({
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
    mutation: GET_REPORT_STATUS,
    variables: { reportId: encodedId, reportType: params.reportType }
  });

  const payload = (
    result.data as { getReportStatus?: GetReportStatusPayload } | undefined
  )?.getReportStatus;
  return {
    error: payload?.errorMessage ?? payload?.message ?? null,
    logs: payload?.reportLogs ?? [],
    status: normalizeReportStatus(payload?.generationStatus)
  };
}

function base64EncodeAscii(value: string): string {
  if (typeof globalThis.btoa === 'function') return globalThis.btoa(value);
  const maybeBuffer = (globalThis as unknown as { Buffer?: typeof Buffer })
    .Buffer;
  if (maybeBuffer) return maybeBuffer.from(value, 'utf8').toString('base64');
  throw new Error('Base64 encoding is not available in this environment');
}
